import { defineStore } from 'pinia';
import api from '@/api/api';
import type {ZProfile} from "@schemas/profile.schemas";
import type {Id} from "@typesDef/general.types";
import {setIdsUnique} from "@/stores/helpers/storeHelper";


export const useProfileStore = defineStore('profile', {
    state: () => ({
        profilesById: {} as Record<Id, ZProfile>,
        myProfileId: null as Id | null, // FIXME this is my profile not the current one
        // FIXME check why this is needed
        currentProfileId: null as Id | null,
    }),
    getters: {
        myProfile(state): ZProfile | null {
            return state.myProfileId ? state.profilesById[state.myProfileId] ?? null : null;
        },
        currentProfile(state): ZProfile | null {
            return state.currentProfileId ? state.profilesById[state.currentProfileId] ?? null : null;
        }
    },
    actions: {
        clearMyData(): void {
            this.profilesById = {};
            this.myProfileId = null;
            this.currentProfileId = null; //in other stores we used currentXXX
        },
        // FIXME move them to general library since they are used in multiple stores
        upsertMany(docs: ZProfile[]) {
            for (const e of docs) this.profilesById[e._id] = e
        },
        /**
         * Fetches a profile by its ID. The fetched profile is added to the "profilesById" map and the "myProfileId" is set to the fetched profile's ID.
         * FIXME here we use just fetchProfile for fetching any profile by id, but it also sets the currentProfileId, which is not correct if we fetch a public profile,
         *  so maybe rename it to "fetchMyProfileById" or something like this
         * @param profileId - The ID of the profile to fetch
         * @returns the fetched profile
         */
        async fetchProfile(profileId: Id): Promise<ZProfile> {
            log.debug(`fetchProfile(${profileId})`);
            const cached = this.profilesById[profileId];
            if(cached) {
                this.currentProfileId = profileId;
                return cached;
            }
            const doc = await api.getDocument<ZProfile>('profiles', profileId);
            this.profilesById[doc._id] = doc;
            this.currentProfileId = doc._id;
            return doc;
        },

        /**
         * Fetches the current profile. The fetched profile is added to the "profilesById" map and the "myProfileId" is set to the fetched profile's ID.
         * @returns the fetched profile
         */
        async fetchMyProfile(): Promise<ZProfile> {
            log.debug(`fetchMyProfile()`);
            if(this.myProfileId) {
                const cached = this.profilesById[this.myProfileId];
                if (cached) {
                    return cached;
                }
            }
            // not set or not cached, fetch it from the backend
            const doc = await api.getDocument<ZProfile>('profiles', 'current');
            this.profilesById[doc._id] = doc;
            this.myProfileId = doc._id;
            return doc;
        },

        /**
         * Fetches a profile by its ID and type.
         * The fetched profile is added to the "profilesById" map and the "currentProfileId" is set to the fetched profile's ID.
         * @param profileId - The ID of the profile to fetch
         * @param profileType - The type of the profile to fetch (default: "profiles")
         * @returns the fetched profile
         */
        async fetchProfileByIdAndType(profileId: Id, profileType: string = "profiles"): Promise<ZProfile> {
            log.debug(`fetchProfileByIdAndType(${profileId}, ${profileType})`);
            const cached = this.profilesById[profileId];
            if(cached) {
                this.currentProfileId = profileId;
                return cached;
            }
            const doc = await api.getDocument<ZProfile>(profileType, profileId);
            this.profilesById[doc._id] = doc;
            this.currentProfileId = doc._id;
            return doc;
        },
        // NOT USED
        async addProfile(profile: ZProfile): Promise<ZProfile> {
            const created = await api.postDocument<ZProfile>('profiles', profile);
            this.profilesById[created._id] = created;
            // add profile to public list since it's public by default
            return created;
        },
        async updateProfile(profile: ZProfile): Promise<ZProfile> {
            const updated = await api.putDocument<ZProfile>('profiles/current', profile);
            this.profilesById [updated._id] = updated;
            return updated;
        },
        async removeProfile(profileId: Id): Promise<void> {
            await api.deleteDocument<ZProfile>('profiles', profileId);
            delete this.profilesById[profileId];
            // if the removed profile is the current one, unset it
            if(this.myProfileId === profileId) {
                this.myProfileId = null;
            }
            // if the removed profile is the public one, unset it
            if(this.currentProfileId === profileId) {
                this.currentProfileId = null;
            }
        },

        async contactProfile(profileId: Id, formData: any): Promise<any> {
            // profiles/67c6f7cb93ce20a17a9247d4/contact
           return await api.postDocument<ZProfile>('profiles/' + profileId + '/contact', formData);
        }
    }
});
